import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import {
  DialogBase,
  DialogService,
  HotkeyService,
  Destroy,
  tr,
  Guard,
  sort,
  DialogRef,
  chainAndForkJoinObservables,
  currentLanguage,
  languageMap,
  BreadCrumbService,
} from '@kpd/framework-ngx';
import { Permissionset } from 'app/shared/models/permissionset';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { takeUntil, tap, catchError, filter, switchMap } from 'rxjs/operators';
import { ApplicationService } from 'app/shared/services/application.service';
import { Application } from 'app/shared/models/application';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { Observable, of } from 'rxjs';
import { PermissionsetFacade } from 'app/shared/services/facades/permissionset.facade';
import { UserManagementEntityUpdater } from 'app/shared/services/entity-updater';
import { PermissionsetsNavigatorHelper } from '../permissionsets-navigator-helper';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-permission-set-wizard-copy',
  templateUrl: './permissionset-details-wizard-copy.component.html',
})
export class PermissionsetWizardCopyComponent extends DialogBase implements OnDestroy {
  // @Input()
  permissionSet: Permissionset;
  canManage: boolean | undefined;
  activeStep = 0;
  copySetLoading = false;
  copyPermissionsLoading = false;
  feedback: { success: boolean; message?: string; name: string; permission: boolean }[] = [];
  applications: Application[] = [];
  nameAndDescriptionColumns = [
    { name: 'name', title: tr('Name'), width: '30%' },
    { name: 'description', title: tr('Description'), width: '70%' },
  ];
  typeOptions = [
    { value: 'assign', label: tr('Assign') },
    { value: 'use', label: tr('Use') },
    { value: 'both', label: tr('Both') },
  ];
  validating = false;

  form: FormGroup;
  languageFields: AbstractControl[];
  currentLanguage = currentLanguage;
  errorMessage: string | null;
  updater: UserManagementEntityUpdater;
  finished = false;

  newPermissionset: Permissionset;

  @ViewChild('feedbackScroll', { static: false })
  feedbackScroll: any;

  @Destroy()
  destroy$: any;

  constructor(
    private permissionSetService: PermissionsetService,
    private permissionSetFacade: PermissionsetFacade,
    private applicationService: ApplicationService,
    private localizationService: UMLocalizationService,
    public navigatorHelper: PermissionsetsNavigatorHelper,
    dialogService: DialogService,
    element: ElementRef,
    hotKeyService: HotkeyService,
    guard: Guard,
    breadCrumbService: BreadCrumbService,
  ) {
    super('copyPermissionSet', dialogService, element, hotKeyService);
    this.updater = new UserManagementEntityUpdater(
      dialogService,
      this.navigatorHelper,
      this.permissionSetFacade,
      guard,
      breadCrumbService,
      'permissionsets',
    );
    this.currentLanguage = this.getLanguage();
  }

  private getLanguage(): string {
    const langCode = this.localizationService.getLanguage();
    if (langCode) {
      return languageMap[langCode];
    }
    return 'english';
  }

  onBeforeShow(dialogRef: DialogRef): void {
    this.permissionSetFacade.detailLoadingStarted();
    this.permissionSetFacade.overviewLoadingStarted();
    this.permissionSet = dialogRef.data as Permissionset;
    this.feedback = [];
    this.activeStep = 0;
    this.validating = false;
    this.errorMessage = null;
    this.canManage = undefined;

    this.permissionSetService
      .canManage(this.permissionSet.key as PermissionsetKey)
      .pipe(
        tap((result) => {
          this.permissionSetFacade.detailLoadingFinished();
          this.permissionSetFacade.overviewLoadingFinished();
          this.canManage = result;
        }),
        filter(() => !!this.canManage),
        switchMap(() => this.applicationService.getApplicationsByTenant()),
      )
      .subscribe((apps) => {
        this.applications = apps;
        const app = this.applications.find((application) => application.name === this.permissionSet.application);
        this.form.patchValue({
          application: app,
        });
      });

    const descriptionsFormGroup = new FormGroup({
      dutch: new FormControl(this.permissionSet.description ? this.permissionSet.description.dutch : undefined, [
        Validators.maxLength(500),
      ]),
      english: new FormControl(this.permissionSet.description ? this.permissionSet.description.english : undefined, [
        Validators.maxLength(500),
      ]),
      french: new FormControl(this.permissionSet.description ? this.permissionSet.description.french : undefined, [
        Validators.maxLength(500),
      ]),
      german: new FormControl(this.permissionSet.description ? this.permissionSet.description.german : undefined, [
        Validators.maxLength(500),
      ]),
    });
    this.form = new FormGroup({
      name: new FormControl(this.permissionSet.name + ' (copy)', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      application: new FormControl(
        this.permissionSet.application ? { name: this.permissionSet.application } : undefined,
        Validators.required,
      ),
      description: new FormControl(
        this.permissionSet.description ? this.permissionSet.description[this.currentLanguage] : undefined,
        Validators.maxLength(500),
      ),
      type: new FormControl(this.permissionSet.type),
      descriptions: descriptionsFormGroup,
    });

    this.form.controls.application.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (
        this.form.controls.name.hasError('permissionSetNameConflictGlobal') ||
        this.form.controls.name.hasError('permissionSetNameConflict')
      ) {
        this.form.controls.name.setErrors(null);
      }
    });

    this.languageFields = Object.values(descriptionsFormGroup.controls);
  }

  hasTranslationSync(translation: string): AbstractControl | null {
    return this.currentLanguage === translation ? this.form.controls.description : null;
  }

  onSavePermissionSet(): void {
    this.form['submitted'] = true;
    if (!this.isValid(this.form)) {
      return;
    }

    const permissionSetKey = new PermissionsetKey(this.formToEntity().name, this.formToEntity().application as string);
    this.validating = true;

    // If the item is new, check if a permission with this key already exists
    // If not, it will throw a 404 error, so in catchError we can actually save the new permissionset
    this.permissionSetService
      .getById(permissionSetKey, true)
      .pipe(
        tap(() => {
          this.validating = false;
          (this.form.get('name') as any).setErrors({
            permissionSetNameConflict: true,
          });
          this.setFieldErrors(true);
        }),
        // Only continue when getById call fails
        filter(() => false),
        catchError(() => {
          permissionSetKey.isGlobalPermission = true;
          return this.permissionSetService.getById(permissionSetKey, true);
        }),
        tap(() => {
          this.validating = false;
          (this.form.get('name') as any).setErrors({ permissionSetNameConflictGlobal: true });
          this.setFieldErrors(true);
        }),
        // Only continue when getById call fails
        filter(() => false),
        catchError(() => {
          this.copySetLoading = true;
          this.activeStep = 1;
          this.validating = false;
          return this.permissionSetFacade.saveItem(this.formToEntity()).pipe(
            tap(() => {
              this.permissionSetFacade.changeSelection(undefined, [this.permissionSet.key]);
            }),
          );
        }),
      )
      .subscribe(
        (result) => {
          this.newPermissionset = result;
          this.feedback.push({ success: true, name: result.name, permission: false });
          this.copySetLoading = false;
          this.copyPermissionsLoading = true;
          const copyPermissionsets = this.copyPermissionsetPermissions(result);
          if (copyPermissionsets) {
            copyPermissionsets.subscribe(() => {
              this.finishCopy();
              this.permissionSetFacade.changeSelection(undefined, [result.key]);
            });
          } else {
            this.finishCopy();
          }
        },
        (err: any) => {
          this.feedback.push({
            success: false,
            message: err.title || tr('Error occurred'),
            name: this.formToEntity().name,
            permission: false,
          });
          this.copySetLoading = false;
        },
      );
  }

  private isValid(form: FormGroup): boolean {
    if (!form.dirty) {
      return true;
    }

    if (form.invalid) {
      this.setFieldErrors(true);
      return false;
    }

    return true;
  }

  private setFieldErrors(hasErrors: boolean): void {
    if (hasErrors) {
      this.errorMessage = tr('Unable to continue, there are one or more invalid fields on your form.');
      return;
    }
    this.errorMessage = '';
  }

  private formToEntity(): Permissionset {
    const permissionSet: Permissionset = new Permissionset();
    permissionSet.name = this.form.value.name;
    permissionSet.type = this.form.value.type;
    if (this.form.value.application) {
      permissionSet.application = this.form.value.application.name;
    }
    permissionSet.description = {};
    permissionSet.description.dutch = this.form.controls.descriptions.value.dutch;
    permissionSet.description.english = this.form.controls.descriptions.value.english;
    permissionSet.description.french = this.form.controls.descriptions.value.french;
    permissionSet.description.german = this.form.controls.descriptions.value.german;

    return permissionSet;
  }

  private copyPermissionsetPermissions(result: Permissionset): Observable<any[]> | null {
    if (this.permissionSet && this.permissionSet.permissions) {
      const observables: Observable<any>[] = [];
      this.permissionSet.permissions.forEach((p) => {
        observables.push(
          this.permissionSetService.addPermissionToPermissionSet(result.key, p.key).pipe(
            tap(() => {
              this.feedback.push({ success: true, name: p.name, permission: true });
            }),
            catchError((error) => {
              this.feedback.push({
                success: false,
                message: error.title || tr('Error occurred'),
                name: p.name,
                permission: true,
              });
              return of([]);
            }),
            tap(() => {
              setTimeout(() => {
                this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
              });
            }),
          ),
        );
      });
      return chainAndForkJoinObservables(observables);
    }
    return null;
  }

  private finishCopy(): void {
    this.copyPermissionsLoading = false;
    this.form.markAsPristine();
    this.permissionSetFacade.detailLoadingFinished();
    this.finished = true;
  }

  onClose(): void {
    if (this.newPermissionset && this.allSucceed) {
      this.close(this.newPermissionset);
      return;
    }
    this.close();
  }

  get copyFailed(): boolean {
    return this.feedback.some((item) => !item.success && !item.permission);
  }

  get failedPermissionsCount(): number {
    return this.feedback.filter((item) => item.permission && !item.success).length;
  }

  get sortedFeedback(): any {
    return sort(this.feedback, [{ name: 'success' }, { name: 'permission' }, { name: 'name' }]);
  }

  get allSucceed(): boolean {
    return !this.feedback.some((item) => !item.success);
  }

  ngOnDestroy(): void {}
}
